import React, { useMemo } from "react";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import styles from "./TopNavigationBar.module.scss";

const TopNavigationBar = ({
    deliveryData,
    deliveryStatistics,
    currentStop,
    elapsedTime,
    isPaused,
    pauseElapsedTime,
    navigationActive,
    startNavigation,
    stopNavigation,
    driverLocation,
    formatTime,
    handlePause,
    deliveryStops,
    onCompleteDelivery,
    onMarkIncomplete,
    onOpenDirections,
    toast,
    optimizationInProgress,
    lastOptimizedTime,
    onOptimizeRoute,
}) => {
    // Calculate delivery completion status
    const deliveryStatus = useMemo(() => {
        if (!deliveryStops || deliveryStops.length === 0) {
            return { showButton: false, type: null };
        }

        const deliveredStops = deliveryStops.filter((stop) => stop.status === "Delivered");
        const incompleteStops = deliveryStops.filter((stop) => stop.status === "Incomplete");
        const skippedStops = deliveryStops.filter((stop) => stop.status === "Skipped");
        const notDeliveredStops = deliveryStops.filter((stop) => stop.status === "Not Delivered");

        // If there are still stops not delivered (pending stops), don't show button
        if (notDeliveredStops.length > 0) {
            return { showButton: false, type: null };
        }

        // If ANY stop is incomplete, show incomplete button
        if (incompleteStops.length > 0) {
            return {
                showButton: true,
                type: "incomplete",
                deliveredCount: deliveredStops.length,
                incompleteCount: incompleteStops.length,
                skippedCount: skippedStops.length,
                totalCount: deliveryStops.length,
            };
        }

        // If all stops are delivered (no incomplete, no skipped, no pending)
        if (deliveredStops.length === deliveryStops.length) {
            return {
                showButton: true,
                type: "complete",
                deliveredCount: deliveredStops.length,
                totalCount: deliveryStops.length,
            };
        }

        // If some delivered and some skipped (no incomplete, no pending)
        if (deliveredStops.length > 0 && skippedStops.length > 0 && incompleteStops.length === 0) {
            return {
                showButton: true,
                type: "complete",
                deliveredCount: deliveredStops.length,
                skippedCount: skippedStops.length,
                totalCount: deliveryStops.length,
            };
        }

        // If all skipped (no delivered, no incomplete, no pending)
        if (skippedStops.length === deliveryStops.length) {
            return {
                showButton: true,
                type: "complete",
                deliveredCount: 0,
                skippedCount: skippedStops.length,
                totalCount: deliveryStops.length,
            };
        }

        return { showButton: false, type: null };
    }, [deliveryStops]);

    const handleCompleteDelivery = () => {
        if (onCompleteDelivery) {
            onCompleteDelivery();
        }
    };

    const handleMarkIncomplete = () => {
        if (onMarkIncomplete) {
            onMarkIncomplete();
        }
    };

    // Generate tooltip text based on delivery status
    const getTooltipText = () => {
        if (!deliveryStatus.showButton) return "";

        if (deliveryStatus.type === "complete") {
            const parts = [];
            if (deliveryStatus.deliveredCount > 0) {
                parts.push(`${deliveryStatus.deliveredCount} delivered`);
            }
            if (deliveryStatus.skippedCount > 0) {
                parts.push(`${deliveryStatus.skippedCount} skipped`);
            }
            return parts.join(", ");
        }

        if (deliveryStatus.type === "incomplete") {
            const parts = [];
            if (deliveryStatus.deliveredCount > 0) {
                parts.push(`${deliveryStatus.deliveredCount} delivered`);
            }
            if (deliveryStatus.incompleteCount > 0) {
                parts.push(`${deliveryStatus.incompleteCount} incomplete`);
            }
            if (deliveryStatus.skippedCount > 0) {
                parts.push(`${deliveryStatus.skippedCount} skipped`);
            }
            return parts.join(", ");
        }

        return "";
    };

    return (
        <div className={styles.topNavbar}>
            <div className={styles.navContent}>
                <div className={styles.deliveryInfo}>
                    <div className={styles.deliveryMeta}>
                        <h1 className={styles.deliveryId}>#{deliveryData.deliveryId}</h1>
                        <Badge value={`${deliveryStatistics?.completedStops || 0}/${deliveryStatistics?.totalStops || 0}`} severity="info" className={styles.stopsBadge} />
                    </div>

                    {currentStop && (
                        <div className={styles.currentStopInfo}>
                            <div className={styles.locationIcon}>
                                <i className="pi pi-map-marker" />
                            </div>
                            <div className={styles.stopDetails}>
                                <span className={styles.nextStopLabel}>Next Stop</span>
                                <h3 className={styles.clientName}>{currentStop.clientName}</h3>
                            </div>
                            <div className={styles.distanceTime}>
                                <div className={styles.metric}>
                                    <i className="pi pi-compass" />
                                    <span>{currentStop.estimatedDistance ? `${currentStop.estimatedDistance.toFixed(1)} km` : "Calculating..."}</span>
                                </div>
                                <div className={styles.metric}>
                                    <i className="pi pi-clock" />
                                    <span>{currentStop.estimatedDuration ? `${Math.round(currentStop.estimatedDuration / 60)} min` : "Calculating..."}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.controls}>
                    {onOptimizeRoute && (
                        <div className={styles.optimizeControl}>
                            <Button
                                label={optimizationInProgress ? "Optimizing..." : "Optimize Route"}
                                icon={optimizationInProgress ? "pi pi-spin pi-spinner" : "pi pi-sort-alt"}
                                severity="info"
                                outlined
                                className={styles.optimizeButton}
                                onClick={onOptimizeRoute}
                                disabled={optimizationInProgress || !driverLocation}
                                tooltip={!driverLocation ? "Driver location required" : lastOptimizedTime ? `Last optimized: ${lastOptimizedTime.toLocaleTimeString()}` : "Reorder stops by road distance"}
                            />
                        </div>
                    )}

                    {deliveryStatus.showButton && (
                        <div className={styles.completionControl}>
                            {deliveryStatus.type === "complete" ? (
                                <Button label="Complete Delivery" icon="pi pi-check-circle" severity="success" className={styles.completeButton} onClick={handleCompleteDelivery} />
                            ) : (
                                <Button label="Mark Incomplete" icon="pi pi-exclamation-triangle" severity="warning" className={styles.incompleteButton} onClick={handleMarkIncomplete} />
                            )}
                        </div>
                    )}

                    <div className={styles.timerControls}>
                        <div className={styles.timers}>
                            <div className={`${styles.timerDisplay} ${isPaused ? styles.paused : ""}`}>
                                <i className="pi pi-clock" />
                                <span className={styles.timeText}>{formatTime(elapsedTime)}</span>
                                {!isPaused && <span className={styles.timerLabel}>Active</span>}
                            </div>

                            {isPaused && pauseElapsedTime > 0 && (
                                <div className={styles.pauseTimer}>
                                    <i className="pi pi-pause-circle" />
                                    <span className={styles.pauseText}>{formatTime(pauseElapsedTime)}</span>
                                    <span className={styles.pauseLabel}>Paused</span>
                                </div>
                            )}
                        </div>

                        <Button icon={isPaused ? "pi pi-play" : "pi pi-pause"} rounded severity={isPaused ? "success" : "warning"} className={styles.pauseButton} onClick={handlePause} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TopNavigationBar;
